import { Injectable, signal } from '@angular/core';
import {
  AiInsight,
  AiInsightSeverity,
  ClientAiInsightsState,
  FeedbackReason,
  FeedbackSentiment,
  InsightFeedback,
  InsightSource,
  InsightType,
} from './client-ai-insights.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers — same djb2 pattern as other state services
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(id: string, key: string, options: readonly T[]): T {
  const h = djb2(`${id}:${key}`);
  return options[h % options.length];
}

function seedInt(id: string, key: string, min: number, max: number): number {
  const h = djb2(`${id}:${key}`);
  return min + (h % (max - min + 1));
}

function seedFloat(id: string, key: string, min: number, max: number): number {
  const h = djb2(`${id}:${key}`);
  return min + ((h % 1000) / 1000) * (max - min);
}

function isoDateTime(daysAgo: number, hourOffset = 9): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hourOffset % 24, 0, 0, 0);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Seed data pools
// ---------------------------------------------------------------------------

const AI_TITLES = [
  'Client expressed concern about retirement timeline',
  'Spending pattern shift detected in recent transactions',
  'Potential 401(k) rollover opportunity identified',
  'Life insurance coverage gap flagged',
  'Emergency fund below recommended threshold',
  'Debt-to-income ratio increased this quarter',
  'Client mentioned job change in recent chat',
  'High-yield savings opportunity based on cash balance',
  'Beneficiary designations not updated in 3+ years',
  'Upcoming college tuition milestone detected',
  'Tax-loss harvesting window identified',
  'Client sentiment trending negative in last 3 interactions',
] as const;

const AI_BODIES = [
  'Based on recent email and chat interactions, the client has raised concerns about whether their current savings rate will support their target retirement age of 62.',
  'Transaction data shows a 23% increase in discretionary spending over the past 60 days, which may impact savings goals.',
  'Client has a 401(k) from a previous employer that has not been rolled over. Current balance estimated at $84,000.',
  'Current life insurance coverage of $250K may be insufficient given recent income increase and new dependent.',
  'Emergency fund currently covers 1.8 months of expenses, below the recommended 3–6 month target.',
  'DTI ratio has risen from 28% to 34% over the past quarter, primarily driven by auto loan origination.',
  'Client mentioned a new position at a different company during last chat session — benefits and 401(k) vesting may be affected.',
  'Current checking balance of $18,400 could earn significantly more in a high-yield savings account.',
  'Last beneficiary review was in 2021. Client has since married and had a child.',
  'Client\'s oldest child starts college in 14 months. No 529 plan is currently on file.',
  'Unrealized losses in taxable account present a tax-loss harvesting opportunity before year-end.',
  'Sentiment analysis across the last 3 interactions shows increased stress language around financial security.',
] as const;

const MANUAL_TITLES = [
  'Follow-up note from annual review meeting',
  'Client preference: prefers email over phone',
  'Discussed estate planning concerns',
  'Client interested in ESG investing options',
  'Noted: client is risk-averse, prefers conservative allocation',
] as const;

const MANUAL_BODIES = [
  'Client wants to revisit asset allocation after Q1. Mentioned possible home purchase in 18 months.',
  'Client strongly prefers email communication. Avoid calling without prior scheduling.',
  'Client expressed interest in updating will and trust documents. Referred to estate attorney.',
  'Client asked about socially responsible investing. Interested in ESG-screened funds.',
  'Client reacted negatively to portfolio volatility discussion. Recommend conservative rebalancing approach.',
] as const;

const TAGS_POOL = [
  'Retirement', 'Savings', 'Debt', 'Insurance', 'Tax', 'Estate',
  'Investment', 'Cash Flow', 'Education', 'Housing', 'Sentiment',
] as const;

const SOURCES: InsightSource[] = ['email', 'sms', 'call', 'chat', 'agent'];
const SEVERITIES: AiInsightSeverity[] = ['info', 'warn', 'danger', 'success'];
const MANUAL_NOTE_ID_PREFIX = 'manual-';

type StoryInsightsSource = 'delta' | 'snapshot' | 'none';

function toRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : undefined;
}

function toCopilotSection(
  section: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!section) return undefined;
  return toRecord(section['copilot']) ?? section;
}

function normalizeInsightSource(source: unknown): InsightSource {
  const raw = String(source ?? '').trim().toLowerCase();
  if (raw === 'email') return 'email';
  if (raw === 'sms') return 'sms';
  if (raw === 'call' || raw === 'phone') return 'call';
  if (raw === 'manual') return 'manual';
  if (raw === 'agent' || raw === 'ai' || raw === 'copilot' || raw === 'assistant_ai') {
    return 'agent';
  }
  return 'chat';
}

function normalizeInsightSeverity(value: unknown): AiInsightSeverity {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'danger' || raw === 'warn' || raw === 'success' || raw === 'info') {
    return raw;
  }
  return 'info';
}

function normalizeInsightTags(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const result: string[] = [];
  for (const item of value) {
    const tag = String(item ?? '').trim();
    if (tag && !result.includes(tag)) {
      result.push(tag);
    }
  }
  return result;
}

function normalizeIso(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) return dt.toISOString();
  }
  return new Date().toISOString();
}

function normalizeConfidence(value: unknown): number | null {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return Math.round(value * 100) / 100;
}

function normalizeStoryInsights(raw: unknown): AiInsight[] {
  if (!Array.isArray(raw)) return [];

  const normalized: AiInsight[] = raw
    .map((item, idx): AiInsight | null => {
      const obj = toRecord(item);
      if (!obj) return null;

      const title = String(obj['title'] ?? obj['text'] ?? '').trim();
      if (!title) return null;

      const body = String(
        obj['description'] ?? obj['body'] ?? obj['text'] ?? title
      ).trim();
      const sourceRefRaw = obj['sourceRef'];
      const sourceRef =
        typeof sourceRefRaw === 'string' && sourceRefRaw.trim()
          ? sourceRefRaw.trim()
          : null;
      const createdAt = normalizeIso(obj['createdAt']);
      const fallbackId = `story-insight-${djb2(`${title}:${createdAt}:${idx}`)}`;

      return {
        id: String(obj['id'] ?? fallbackId),
        type: obj['type'] === 'manual' ? 'manual' : 'ai',
        title,
        body,
        source: normalizeInsightSource(obj['source']),
        severity: normalizeInsightSeverity(obj['severity']),
        createdAt,
        confidence: normalizeConfidence(obj['confidence']),
        agentGenerated: Boolean(obj['agentGenerated']),
        seen: false,
        feedback: null,
        sourceRef,
        tags: normalizeInsightTags(obj['tags']),
      };
    })
    .filter((insight): insight is AiInsight => insight !== null);

  normalized.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return normalized;
}

function extractStoryInsights(raw: unknown): {
  source: StoryInsightsSource;
  insights: AiInsight[];
} {
  const payload = toRecord(raw);
  if (!payload) return { source: 'none', insights: [] };

  const deltaSection = toCopilotSection(toRecord(payload['delta']));
  if (deltaSection && 'aiInsights' in deltaSection) {
    return {
      source: 'delta',
      insights: normalizeStoryInsights(deltaSection['aiInsights']),
    };
  }

  const snapshotSection = toCopilotSection(toRecord(payload['snapshot']));
  if (snapshotSection && 'aiInsights' in snapshotSection) {
    return {
      source: 'snapshot',
      insights: normalizeStoryInsights(snapshotSection['aiInsights']),
    };
  }

  return { source: 'none', insights: [] };
}

function mergeStoryInsights(existing: AiInsight[], incoming: AiInsight[]): AiInsight[] {
  // Incremental merge: keep existing insights and upsert incoming by ID.
  // Preserve seen/feedback for previously known IDs.
  const mergedById = new Map(existing.map((insight) => [insight.id, insight]));
  for (const incomingInsight of incoming) {
    const existingInsight = mergedById.get(incomingInsight.id);
    mergedById.set(incomingInsight.id, {
      ...incomingInsight,
      seen: existingInsight?.seen ?? false,
      feedback: existingInsight?.feedback ?? null,
    });
  }
  return [...mergedById.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function replaceStoryInsights(existing: AiInsight[], incoming: AiInsight[]): AiInsight[] {
  // Snapshot replace: incoming list is authoritative for story-provided insights.
  // Preserve local manual notes and preserve seen/feedback for matching IDs.
  const existingById = new Map(existing.map((insight) => [insight.id, insight]));
  const localManualNotes = toRuntimeManualNotes(existing);
  const localManualById = new Set(localManualNotes.map((insight) => insight.id));

  const replaced = incoming.map((incomingInsight) => {
    const existingInsight = existingById.get(incomingInsight.id);
    return {
      ...incomingInsight,
      seen: existingInsight?.seen ?? false,
      feedback: existingInsight?.feedback ?? null,
    };
  });

  const merged = [
    ...localManualNotes,
    ...replaced.filter((insight) => !localManualById.has(insight.id)),
  ];
  return merged.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function stepDemoId(stepKey: string): string {
  const idx = stepKey.indexOf(':');
  return idx >= 0 ? stepKey.slice(0, idx) : stepKey;
}

function toRuntimeManualNotes(insights: AiInsight[]): AiInsight[] {
  return insights.filter(
    (insight) =>
      insight.type === 'manual' && insight.id.startsWith(MANUAL_NOTE_ID_PREFIX)
  );
}

function stepNumber(stepKey: string): number | null {
  const idx = stepKey.indexOf(':');
  if (idx < 0 || idx >= stepKey.length - 1) return null;
  const raw = stepKey.slice(idx + 1).trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

function recomputeMetrics(
  sessionId: string,
  insights: AiInsight[],
  previous: ClientAiInsightsState | null
): Pick<
  ClientAiInsightsState,
  | 'newInsights24h'
  | 'newInsights7d'
  | 'highPriorityCount'
  | 'agentSuggestionCount'
  | 'timeSavedMinutes'
> {
  const now = new Date();
  const cutoff24h = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  const cutoff7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const aiInsights = insights.filter((ins) => ins.type === 'ai');

  return {
    newInsights24h: aiInsights.filter((ins) => ins.createdAt >= cutoff24h).length,
    newInsights7d: aiInsights.filter((ins) => ins.createdAt >= cutoff7d).length,
    highPriorityCount: aiInsights.filter(
      (ins) => ins.severity === 'danger' || ins.severity === 'warn'
    ).length,
    agentSuggestionCount: aiInsights.filter((ins) => ins.agentGenerated).length,
    timeSavedMinutes:
      previous?.timeSavedMinutes ?? seedInt(sessionId, 'timeSaved', 18, 72),
  };
}

// ---------------------------------------------------------------------------
// Seeding function
// ---------------------------------------------------------------------------

function seedInsights(sessionId: string): AiInsight[] {
  const count = seedInt(sessionId, 'count', 10, 16);
  const insights: AiInsight[] = [];

  for (let i = 0; i < count; i++) {
    const key = `${sessionId}:insight:${i}`;
    const isManual = i >= count - 2; // last 2 are always manual notes
    const type: InsightType = isManual ? 'manual' : 'ai';

    const titleIdx = seedInt(key, 'title', 0, isManual ? MANUAL_TITLES.length - 1 : AI_TITLES.length - 1);
    const bodyIdx = seedInt(key, 'body', 0, isManual ? MANUAL_BODIES.length - 1 : AI_BODIES.length - 1);

    const title = isManual ? MANUAL_TITLES[titleIdx] : AI_TITLES[titleIdx];
    const body = isManual ? MANUAL_BODIES[bodyIdx] : AI_BODIES[bodyIdx];

    const daysAgo = seedInt(key, 'days', 0, 30);
    const hourOffset = seedInt(key, 'hour', 7, 18);

    const source: InsightSource = isManual
      ? 'manual'
      : seedPick(key, 'source', SOURCES);

    const severityIdx = seedInt(key, 'severity', 0, SEVERITIES.length - 1);
    const severity: AiInsightSeverity = isManual ? 'info' : SEVERITIES[severityIdx];

    const tagCount = seedInt(key, 'tagCount', 1, 3);
    const tags: string[] = [];
    for (let t = 0; t < tagCount; t++) {
      const tag = seedPick(`${key}:tag${t}`, 'tag', TAGS_POOL);
      if (!tags.includes(tag)) tags.push(tag);
    }

    const agentGenerated = !isManual && seedInt(key, 'agent', 0, 3) === 0;
    const confidence = isManual ? null : Math.round(seedFloat(key, 'conf', 0.55, 0.97) * 100) / 100;
    const seen = daysAgo > 2;

    insights.push({
      id: `insight-${sessionId.slice(-6)}-${i}`,
      type,
      title,
      body,
      source,
      severity,
      createdAt: isoDateTime(daysAgo, hourOffset),
      confidence,
      agentGenerated,
      seen,
      feedback: null,
      sourceRef: isManual ? null : `ref-${djb2(key) % 9000 + 1000}`,
      tags,
    });
  }

  // Sort newest first
  return insights.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function buildState(sessionId: string): ClientAiInsightsState {
  const insights = seedInsights(sessionId);
  const now = new Date();
  const cutoff24h = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
  const cutoff7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const aiInsights = insights.filter((i) => i.type === 'ai');
  const newInsights24h = aiInsights.filter((i) => i.createdAt >= cutoff24h).length;
  const newInsights7d = aiInsights.filter((i) => i.createdAt >= cutoff7d).length;
  const highPriorityCount = insights.filter((i) => i.severity === 'danger' || i.severity === 'warn').length;
  const agentSuggestionCount = insights.filter((i) => i.agentGenerated).length;
  const timeSavedMinutes = seedInt(sessionId, 'timeSaved', 18, 72);

  return {
    insights,
    newInsights24h,
    newInsights7d,
    highPriorityCount,
    agentSuggestionCount,
    timeSavedMinutes,
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable()
export class ClientAiInsightsStateService {
  private readonly _state = signal<Record<string, ClientAiInsightsState>>({});
  private readonly _activeStepKeyBySession = signal<Record<string, string>>({});
  private readonly _storyRevisionBySession = signal<Record<string, number>>({});

  readonly state = this._state.asReadonly();

  ensureState(sessionId: string): void {
    if (!this._state()[sessionId]) {
      this._state.update((prev) => ({
        ...prev,
        [sessionId]: buildState(sessionId),
      }));
    }
    if (!(sessionId in this._activeStepKeyBySession())) {
      this._activeStepKeyBySession.update((prev) => ({
        ...prev,
        [sessionId]: '',
      }));
    }
    if (!(sessionId in this._storyRevisionBySession())) {
      this._storyRevisionBySession.update((prev) => ({
        ...prev,
        [sessionId]: 0,
      }));
    }
  }

  getState(sessionId: string): ClientAiInsightsState | null {
    return this._state()[sessionId] ?? null;
  }

  /**
   * Applies Story Service step payload to AI insights for a session.
   * Story insights are accumulated across demo steps.
   * Manual notes created in-app are always preserved.
   */
  applyStepPayload(sessionId: string, raw: unknown, stepKey: string): void {
    this.ensureState(sessionId);
    const currentStepKey = this._activeStepKeyBySession()[sessionId] ?? '';
    if (currentStepKey === stepKey) return;

    const extracted = extractStoryInsights(raw);
    const current = this._state()[sessionId] ?? buildState(sessionId);
    const previousDemoId = stepDemoId(currentStepKey);
    const nextDemoId = stepDemoId(stepKey);
    const previousStep = stepNumber(currentStepKey);
    const nextStep = stepNumber(stepKey);
    const isNonForwardStep =
      previousStep !== null && nextStep !== null && nextStep <= previousStep;
    const startsFreshStoryRun =
      currentStepKey === '' ||
      currentStepKey === 'fallback' ||
      previousDemoId !== nextDemoId ||
      isNonForwardStep;
    const preserveInitialManualNotes = currentStepKey === '';
    const freshRunBaseline = preserveInitialManualNotes
      ? toRuntimeManualNotes(current.insights)
      : [];

    if (extracted.source === 'none') {
      // Keep existing accumulation for normal forward steps with no aiInsights.
      // On demo switch/reset/rewind, clear the current run.
      if (startsFreshStoryRun) {
        const baselineInsights = freshRunBaseline;
        const metrics = recomputeMetrics(sessionId, baselineInsights, current);
        this._state.update((prev) => ({
          ...prev,
          [sessionId]: {
            ...current,
            insights: baselineInsights,
            ...metrics,
          },
        }));
      }
      this._activeStepKeyBySession.update((prev) => ({
        ...prev,
        [sessionId]: stepKey,
      }));
      return;
    }

    const baselineInsights = startsFreshStoryRun ? freshRunBaseline : current.insights;
    const mergedInsights =
      extracted.source === 'snapshot'
        ? replaceStoryInsights(baselineInsights, extracted.insights)
        : mergeStoryInsights(baselineInsights, extracted.insights);
    const metrics = recomputeMetrics(sessionId, mergedInsights, current);

    this._state.update((prev) => ({
      ...prev,
      [sessionId]: {
        ...current,
        insights: mergedInsights,
        ...metrics,
      },
    }));
    this._activeStepKeyBySession.update((prev) => ({
      ...prev,
      [sessionId]: stepKey,
    }));
    this._storyRevisionBySession.update((prev) => ({
      ...prev,
      [sessionId]: (prev[sessionId] ?? 0) + 1,
    }));
  }

  /**
   * Switches the session to local seeded fallback state mode.
   * Does not increment story revision (no "new Story update" signal).
   */
  loadFallback(sessionId: string): void {
    this.ensureState(sessionId);
    this._activeStepKeyBySession.update((prev) => ({
      ...prev,
      [sessionId]: 'fallback',
    }));
  }

  storyRevision(sessionId: string): number {
    return this._storyRevisionBySession()[sessionId] ?? 0;
  }

  submitFeedback(
    sessionId: string,
    insightId: string,
    sentiment: FeedbackSentiment,
    reason: FeedbackReason | null,
    comment: string,
  ): void {
    const s = this._state()[sessionId];
    if (!s) return;
    const feedback: InsightFeedback = {
      sentiment,
      reason,
      comment,
      submittedAt: new Date().toISOString(),
    };
    this._state.update((prev) => ({
      ...prev,
      [sessionId]: {
        ...s,
        insights: s.insights.map((ins) =>
          ins.id === insightId ? { ...ins, feedback, seen: true } : ins
        ),
      },
    }));
  }

  addManualNote(
    sessionId: string,
    title: string,
    body: string,
    timestamp: string,
  ): void {
    const s = this._state()[sessionId];
    if (!s) return;
    const newNote: AiInsight = {
      id: `${MANUAL_NOTE_ID_PREFIX}${Date.now()}`,
      type: 'manual',
      title: title.trim() || 'Advisor Note',
      body,
      source: 'manual',
      severity: 'info',
      createdAt: timestamp,
      confidence: null,
      agentGenerated: false,
      seen: true,
      feedback: null,
      sourceRef: null,
      tags: [],
    };
    this._state.update((prev) => ({
      ...prev,
      [sessionId]: {
        ...s,
        insights: [newNote, ...s.insights],
      },
    }));
  }

  markAllSeen(sessionId: string): void {
    const s = this._state()[sessionId];
    if (!s) return;
    this._state.update((prev) => ({
      ...prev,
      [sessionId]: {
        ...s,
        insights: s.insights.map((ins) => ({ ...ins, seen: true })),
      },
    }));
  }

  unseenCount(sessionId: string): number {
    return this._state()[sessionId]?.insights.filter((i) => !i.seen && i.type === 'ai').length ?? 0;
  }
}
